import { _ } from "../../../polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import AbstractImporter from "../AbstractImporter.js";
import ImportOptionsSelectionStep from "./ImportOptionsSelectionStep.js";
import session from "../../../session.js";
import TokenStep from "../TokenStep.js";
import TripletexIntegration from "../../../../models/integration/TripletexIntegration.js";

/**
 * `TripletexImporter` imports from Tripletex integration API.
 */
export default class TripletexImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._fiscalYearsRepository = makeMonitorRepository({
			route: "/Shared/Import/TripletexFiscalYears",
			antiForgeryToken,
		});

		this._tripletexImportRepository = makeMonitorRepository({
			route: "/Shared/Import/Tripletex",
			antiForgeryToken,
		});
	}

	_wizardSteps() {
		return [
			this._createEmployeeTokenStep(),
			this._createFiscalYearSelectionStep(),
		];
	}

	// Step 1: let the user type his/her employee token. If there is
	// one already, try it before asking.
	_createEmployeeTokenStep() {
		return new TokenStep({
			label: _("User key"),
			hasToken: () => this._context.company.hasTripletexIntegration(),
			attribute: "employeeToken",
			submitCallback: this._employeeTokenToFiscalYears.bind(this),
		});
	}

	// Step 1 ⇒ Step 2: get the list of available fiscal years
	// associated with the employee token.
	_employeeTokenToFiscalYears({ onDone, step }) {
		const employeeToken = (this._context.employeeToken || "").trim();
		this._context.employeeToken = employeeToken;

		this._context.includeInvoices =
			this._context.company.getTripletexIntegrationInvoiceImportOption();

		const companyId = this._companyId;

		this._fiscalYearsRepository
			.get({
				filter: { employeeToken, companyId },
				notify: false,
			})
			.then((fiscalYears) => {
				if (fiscalYears.length === 0) {
					step.addSubmitError(
						_(
							"No years found to import. Please check the settings in Tripletex.",
						),
					);
				}
				this._context.fiscalYears = fiscalYears;

				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	// Step 2: let the user select a fiscal year to import.
	_createFiscalYearSelectionStep() {
		return new ImportOptionsSelectionStep({
			submitCallback: this._fiscalYearToImportData.bind(this),
		});
	}

	// Step 2 ⇒ Step 3: get all the data corresponding to the selected
	// fiscal year (e.g., budgets, dimensions, dates, …)
	_fiscalYearToImportData({ onDone, step }) {
		this._tripletexImportRepository
			.save({
				model: {
					employeeToken: this._context.employeeToken,
					companyId: this._companyId,
					fiscalYear: this._context.fiscalYear,
					includeInvoices: this._context.includeInvoices,
				},
				notify: false,
			})
			.then((data) => {
				this._context.importData = data;
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	_errorToMessage(error) {
		switch (error.responseJSON.typeOfError) {
			case "InvalidEmployeeToken":
			case "InvalidConsumerToken":
			case "InvalidLoginCredential": {
				return _("The provided user key is invalid");
			}
			case "InvalidDataFormat": {
				return _(
					"There have been a data conversion problem during the import. Please contact support.",
				);
			}
			case "UnknownApiError": {
				return _(
					"At the moment there seems to be problems with the {externalAPI} server. Please try again in a few minutes.",
					{ externalAPI: "Tripletex API" },
				);
			}
			case "InternalServerError": {
				return error.responseJSON.message;
			}
			case "SupplierInformationMissing": {
				return _(
					"The invoice with number '{invoiceInfo}' does not include supplier information",
					{ invoiceInfo: error.responseJSON.message },
				);
			}
			case "VoucherRetrievalFailure": {
				return _(
					"The vouchers '{voucherList}' can not be retrieved, please contact support.",
					{ voucherList: error.responseJSON.message },
				);
			}

			case "UserNotValidated": {
				return _(
					"The user is not validated, please contact Tripletex support.",
					{ voucherList: error.responseJSON.message },
				);
			}

			case "BadRequest": {
				return error.responseJSON.message;
			}
			default: {
				return _(
					"We are having problems with the import from {externalAPI}. It might just be a temporary problem so please try again in a few minutes or contact support if the problem remains.",
					{ externalAPI: "Tripletex API" },
				);
			}
		}
	}
}

TripletexImporter.integrationType = TripletexIntegration.getType();
